/** SCR-09 / 10 — a partner's view of one lead: details, conversation, documents and (company admin) team assignment. */
import { useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { PageHeader, Card, Tabs, Spinner, ErrorState, Field } from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import { pushToast } from '../ui/uiSlice.js';
import { money, date, dateTime } from '../../utils/formatters.js';
import * as api from '../../api/partnerPortal.api.js';
import { SubmissionBadge, errorText } from './partnerShared.jsx';
import { LeadFormModal } from './PartnerLeadsPage.jsx';
import { Conversation, Documents } from './LeadCollab.jsx';

const Row = ({ label, children }) => (<div className="col-md-6"><div className="field-x-label mb-1">{label}</div><div className="t-sm" style={{ whiteSpace: 'pre-wrap' }}>{children || '—'}</div></div>);

export default function PartnerLeadDetailPage() {
  const { id } = useParams();
  const me = useOutletContext();
  const qc = useQueryClient();
  const dispatch = useDispatch();
  const [tab, setTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const q = useQuery({ queryKey: ['partner', 'lead', id], queryFn: () => api.getLead(id) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['partner'] });
  const submit = useMutation({
    mutationFn: () => api.submitDraft(id),
    onSuccess: () => { refresh(); dispatch(pushToast({ tone: 'success', message: 'Submitted for review' })); },
    onError: (e) => dispatch(pushToast({ tone: 'danger', message: 'Could not submit', detail: errorText(e) })),
  });

  if (q.isLoading) return <Spinner />;
  if (q.isError) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const l = q.data;
  const isAdmin = me.role === 'admin';
  const canEdit = ['draft', 'information_requested'].includes(l.submission_status);
  const rejected = l.submission_status === 'rejected';

  return (
    <>
      <PageHeader title={l.lead_name} subtitle={`${l.partner_lead_id} · ${l.contact_person}`} breadcrumb={<Link to="/partner/leads"><i className="bi bi-arrow-left me-1" aria-hidden="true" />Deals / Leads</Link>}>
        <SubmissionBadge status={l.submission_status} />
        {canEdit && <button type="button" className="btn btn-edit btn-sm-x" onClick={() => setEditing(true)}><i className="bi bi-pencil me-1" aria-hidden="true" />Edit</button>}
        {l.submission_status === 'draft' && <button type="button" className="btn btn-save btn-sm-x" disabled={submit.isPending} onClick={() => submit.mutate()}>{submit.isPending ? <ButtonSpinner /> : 'Submit for review'}</button>}
      </PageHeader>

      {l.submission_status === 'information_requested' && (
        <div className="alert alert-warning t-sm"><b>The Sales Manager needs more information.</b> Reply in the Conversation tab, then edit the lead if details changed — saving sends it back to review.</div>
      )}
      {rejected && <div className="alert alert-danger t-sm"><b>This lead was rejected.</b> {l.rejection_reason}</div>}
      {l.submission_status === 'approved' && <div className="alert alert-success t-sm"><b>Approved.</b> It is now in the CRM{l.crm_lead?.lead_no ? ` as ${l.crm_lead.lead_no}` : ''}{l.crm_lead?.status ? ` — status ${l.crm_lead.status}` : ''}.</div>}

      <div className="row g-3">
        <div className="col-lg-8">
          <Card bare>
            <div className="px-3 pt-2"><Tabs value={tab} onChange={setTab} tabs={[{ key: 'overview', label: 'Overview' }, { key: 'conversation', label: 'Conversation' }, { key: 'documents', label: 'Documents' }, ...(isAdmin ? [{ key: 'assignment', label: 'Team assignment' }] : [])]} /></div>
            <div className="p-3">
              {tab === 'overview' && (
                <div className="row g-3">
                  <Row label="Lead / Company">{l.lead_name}</Row><Row label="Contact person">{l.contact_person}</Row>
                  <Row label="Designation">{l.designation}</Row><Row label="Email">{l.email}</Row>
                  <Row label="Mobile">{l.mobile}</Row><Row label="Website">{l.website}</Row>
                  <Row label="Industry">{l.industry}</Row><Row label="Country">{l.country}</Row>
                  <Row label="State / City">{l.state_city}</Row><Row label="Product / Service interest">{l.product_interest}</Row>
                  <Row label="Estimated deal value">{l.estimated_value != null ? money(l.estimated_value, l.currency) : null}</Row><Row label="Purchase timeline">{l.purchase_timeline}</Row>
                  <div className="col-12"><div className="field-x-label mb-1">Description</div><div className="t-sm" style={{ whiteSpace: 'pre-wrap' }}>{l.description}</div></div>
                </div>
              )}
              {tab === 'conversation' && <Conversation leadId={id} locked={rejected} />}
              {tab === 'documents' && <Documents leadId={id} locked={rejected} />}
              {tab === 'assignment' && isAdmin && <Assignment lead={l} onChanged={refresh} />}
            </div>
          </Card>
        </div>
        <div className="col-lg-4">
          <Card title="Summary" bodyClass="d-flex flex-column gap-2 t-sm">
            <div><div className="field-x-label">Submitted by</div>{l.submitted_by.name}</div>
            <div><div className="field-x-label">Submitted on</div>{l.submitted_at ? dateTime(l.submitted_at) : 'Not submitted yet'}</div>
            {isAdmin && <div><div className="field-x-label">Assigned to (team)</div>{l.assigned_member?.name ?? '—'}</div>}
            <div><div className="field-x-label">Sales representative</div>{l.sales_rep?.name ?? 'Not assigned yet'}</div>
            {l.crm_lead && <div><div className="field-x-label">CRM lead</div>{l.crm_lead.lead_no} · {l.crm_lead.status}</div>}
            {l.approved_at && <div><div className="field-x-label">Approved on</div>{date(l.approved_at)}</div>}
            {l.review_remarks && <div><div className="field-x-label">Reviewer remarks</div>{l.review_remarks}</div>}
          </Card>
        </div>
      </div>
      <LeadFormModal open={editing} lead={l} onClose={() => setEditing(false)} onDone={() => { setEditing(false); refresh(); }} />
    </>
  );
}

function Assignment({ lead, onChanged }) {
  const dispatch = useDispatch();
  const team = useQuery({ queryKey: ['partner', 'team'], queryFn: api.listTeam });
  const hist = useQuery({ queryKey: ['partner', 'assignment', lead.id], queryFn: () => api.assignmentHistory(lead.id) });
  const [to, setTo] = useState('');
  const [remarks, setRemarks] = useState('');
  const save = useMutation({
    mutationFn: () => api.assignToMember(lead.id, { assigned_to: to, remarks: remarks.trim() || undefined }, Boolean(lead.assigned_member)),
    onSuccess: () => { setTo(''); setRemarks(''); onChanged(); dispatch(pushToast({ tone: 'success', message: 'Assignment saved' })); },
    onError: (e) => dispatch(pushToast({ tone: 'danger', message: 'Could not assign', detail: errorText(e) })),
  });
  const active = (team.data ?? []).filter((m) => m.status === 'active');
  return (
    <div>
      <div className="t-sm mb-2">Currently assigned to: <b>{lead.assigned_member?.name ?? 'nobody'}</b></div>
      <div className="alert alert-info py-2 t-xs">Assigning is internal bookkeeping. The assigned member does <b>not</b> gain access to a lead someone else submitted.</div>
      <div className="row g-2 align-items-end mb-3">
        <div className="col-md-5"><Field label="Assign to"><select className="form-select" value={to} onChange={(e) => setTo(e.target.value)}><option value="">Choose a member…</option>{active.map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}</select></Field></div>
        <div className="col-md-5"><Field label="Remarks"><input className="form-control" value={remarks} onChange={(e) => setRemarks(e.target.value)} maxLength={500} /></Field></div>
        <div className="col-md-2"><button type="button" className="btn btn-save btn-sm-x w-100" disabled={!to || save.isPending} onClick={() => save.mutate()}>{save.isPending ? <ButtonSpinner /> : lead.assigned_member ? 'Change' : 'Assign'}</button></div>
      </div>
      <div className="eyebrow mb-2">Assignment history</div>
      {(hist.data ?? []).length === 0 ? <div className="t-sm text-muted">No assignments yet.</div> : (
        <div className="table-x-wrap"><table className="table-x is-dense"><thead><tr><th>Assigned to</th><th>By</th><th>On</th><th>Remarks</th></tr></thead>
          <tbody>{hist.data.map((h) => (<tr key={h.id}><td className="fw-600">{h.assigned_to}{h.is_current && <span className="pill pill-emerald ms-1">current</span>}</td><td>{h.assigned_by}</td><td>{dateTime(h.assigned_at)}</td><td>{h.remarks ?? '—'}</td></tr>))}</tbody></table></div>
      )}
    </div>
  );
}
